import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './AuthContext';
import { brokerApi } from './api';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import ConnectBroker from './ConnectBroker';
import SecuritySettings from './SecuritySettings';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import TradingSignalsModule from './TradingSignals';
import ExecuteTradeModule from './ExecuteTrade';
import OrdersModule from './Orders';
import PortfolioAnalysisModule from './PortfolioAnalysis';
import MarketResearchModule from './MarketResearch';
import AIManagerModule from './AIManager';
import RiskDashboardModule from './RiskDashboard';
import StockAnalyzerPage from './StockAnalyzerPage';
import BillingPage from './BillingPage';
import WatchlistsPage from './WatchlistsPage';
import MarketsPage from './MarketsPage';
import SettingsPage from './SettingsPage';
import LegalPage from './LegalPage';
import AppFooter from './AppFooter';
import ErrorBoundary from './ErrorBoundary';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="app-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

// Wraps routes that require an active plan. Expired trialists and canceled subscribers
// are redirected to /billing so they can upgrade — billing/security are always reachable.
function PlanGate({ children }) {
  const { user } = useAuth();
  if (!user) return children;

  const trialExpired =
    user.plan !== 'lifetime' &&
    user.planStatus === 'active' &&
    user.planExpiresAt &&
    new Date(user.planExpiresAt) < new Date();
  const canceled = user.planStatus === 'canceled';
  const pastDue = user.planStatus === 'past_due';

  if (trialExpired || canceled || pastDue) {
    return <Navigate to="/billing" replace />;
  }
  return children;
}

function MainApp() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [tradePrefill, setTradePrefill] = useState(null);
  const location = useLocation();

  useEffect(() => {
    brokerApi.status().then(setBrokerStatus).catch(() => setBrokerStatus({ connected: false }));
  }, []);

  // Lets the Stock Analyzer page's "Trade" button hand off a symbol via navigation state,
  // since it's a separate top-level route and can't reach this component's local state directly.
  useEffect(() => {
    if (location.state?.tradeSymbol) {
      setTradePrefill({ symbol: location.state.tradeSymbol });
      setActiveTab('trade');
    }
  }, [location.state]);

  const openTradeWith = (prefill) => {
    setTradePrefill(prefill);
    setActiveTab('trade');
  };

  return (
    <div className="quantumtrade-app">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} brokerStatus={brokerStatus} />

      <main className="app-main">
        <ErrorBoundary key={activeTab}>
          {activeTab === 'dashboard' && <Dashboard />}
          {activeTab === 'signals' && <TradingSignalsModule />}
          {activeTab === 'trade' && (
            <ExecuteTradeModule onViewOrders={() => setActiveTab('orders')} prefill={tradePrefill} />
          )}
          {activeTab === 'orders' && <OrdersModule />}
          {activeTab === 'analysis' && <PortfolioAnalysisModule />}
          {activeTab === 'research' && <MarketResearchModule />}
          {activeTab === 'manager' && <AIManagerModule onTradeAction={openTradeWith} />}
          {activeTab === 'risk' && <RiskDashboardModule />}
        </ErrorBoundary>
        <AppFooter />
      </main>
    </div>
  );
}

export default function QuantumTrade() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Billing and security are always reachable so expired users can upgrade */}
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/security"
            element={
              <ProtectedRoute>
                <SecuritySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connect-broker"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <ConnectBroker />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stocks/:symbol"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <StockAnalyzerPage />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <MainApp />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <OrdersModule />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/watchlists"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <WatchlistsPage />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/markets"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <MarketsPage />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <SettingsPage />
                </PlanGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/legal"
            element={
              <ProtectedRoute>
                <PlanGate>
                  <LegalPage />
                </PlanGate>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}
